// FRU-24088: Sentry events that aren't failures of our code. They can't be fixed by changing it, and they hid the ones that
// can: the Facebook and Instagram in-app browsers' own injected scripts throwing in our page (ANGULAR-6YE, 71F, 6YM, 6Q2),
// and a crawler on a 2016 browser engine the site doesn't support (ANGULAR-7D4, 7D5, 7D6).

export type NotOurErrorReason = 'host_app_script' | 'bot_or_outdated_browser';

/** Functions and script addresses of the in-app browsers' injected performance loggers. */
const HOST_APP_FUNCTIONS = ['sendDataToNative', 'processLargestContentfulPain', 'sendJsBlockingTimeMessage'];
const HOST_APP_FILE = /^iabjs:\/\/|navigation_performance_logger/i;
/** What those scripts throw when the app's native bridge is gone or was never there. */
const HOST_APP_MESSAGES = [/Java object is gone/i, /window\.webkit\.messageHandlers/i, /window\.originalonload/i];
/** Our own code's bundles: the apps' main and lazy chunks, and the thank-you widget. */
const OUR_APP_BUNDLE = /\/(?:main|chunk|thank-you)(?:-[^/?#]*)?\.js(?:[?#]|$)/i;

/** The oldest Chrome or Chromium engine the site supports. */
export const OLDEST_SUPPORTED_CHROMIUM = 80;

/** A crawler, a headless browser, or a Chrome or Chromium engine older than the site supports. */
export function botOrOutdatedBrowser(userAgent: string): boolean {
  const ua = String(userAgent || '');
  if (!ua) { return false; }
  if (/googlebot|adsbot|bingbot|applebot|duckduckbot|yandex(?:bot)?\/|baiduspider|facebookexternalhit|crawler|spider|headlesschrome|lighthouse|pagespeed|\bbot\/\d/i.test(ua)) { return true; }
  const chromium = /Chrom(?:e|ium)\/(\d+)/.exec(ua);
  return !!chromium && Number(chromium[1]) < OLDEST_SUPPORTED_CHROMIUM;
}

/** Why a Sentry event isn't an error of ours, or null when it may be. */
export function notOurError(event: any, userAgent: string): NotOurErrorReason | null {
  if (botOrOutdatedBrowser(userAgent)) { return 'bot_or_outdated_browser'; }
  const values: any[] = (event && event.exception && Array.isArray(event.exception.values)) ? event.exception.values : [];
  // Sentry lists linked errors oldest first, so the last one is the error that was reported. Only it decides: an injected
  // script showing up as a linked cause doesn't make our own error someone else's.
  const reported = values.length ? values[values.length - 1] : null;
  if (!reported) {
    return HOST_APP_MESSAGES.some((pattern) => pattern.test(String((event && event.message) || ''))) ? 'host_app_script' : null;
  }
  const frames: any[] = (reported.stacktrace && Array.isArray(reported.stacktrace.frames)) ? reported.stacktrace.frames : [];
  // A stack is listed oldest call first, so the last frame is the one that threw. An injected script can call our code,
  // and an error our code throws then still has the injected frames below it.
  const thrower = frames.length ? frames[frames.length - 1] : null;
  if (thrower) {
    const file = String(thrower.filename || thrower.abs_path || '');
    if (HOST_APP_FILE.test(file) || HOST_APP_FUNCTIONS.indexOf(String(thrower.function || '')) >= 0) {
      return 'host_app_script';
    }
  }
  // Our code never touches these identifiers, so only the injected scripts raise these messages. The injected function has
  // no frame of its own, so the frame that threw can be a wrapper in the polyfills bundle (zone.js, ANGULAR-6Q2). An error
  // thrown in our own app bundles is never dropped on its message alone.
  const thrownInOurCode = !!thrower && OUR_APP_BUNDLE.test(String(thrower.filename || thrower.abs_path || ''));
  return !thrownInOurCode && HOST_APP_MESSAGES.some((pattern) => pattern.test(String(reported.value || '')))
    ? 'host_app_script' : null;
}
